import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVideoPageStateMachine } from '../hooks/useVideoPageStateMachine';
import { getImageSizes, getVoices, getBgMusicList, getSubtitleTemplates } from '../utils/api';
import InputBar from '../components/InputBar';
import Tutorial from '../components/Tutorial';
import { BgMusic } from '../components/BgMusicSelector';
import type { SubtitleTemplate, SubtitleStyle } from '../types';

// 优化后的分镜数据结构
interface SceneItem {
  id: string;
  desc: string;
  prompt: string;
  dialogue: string;
  images: string[];
  videoUrl: string;
  audioUrl: string;
  status: string;
  cursorPosition?: { startTime: number; endTime: number };
  waveformData?: number[];
  audioPlayState?: boolean;
  videoLoading?: boolean;
}

const VideoInputPage: React.FC = () => {
  const [inputContent, setInputContent] = useState('');
  const [quality, setQuality] = useState('standard');
  const [resolution, setResolution] = useState('864x1536');
  const [imageSizes, setImageSizes] = useState<string[]>([]); // 分辨率列表
  const [, setSceneList] = useState<SceneItem[]>([]); // sceneList not used
  const [showTutorial] = useState(true); // setShowTutorial not used

  // 音色相关状态
  const [voices, setVoices] = useState<{ voice_id: string; name: string; voice_type?: string }[]>([]);
  const [selectedVoice, setSelectedVoice] = useState('');

  // 背景音乐相关状态
  const [bgMusicList, setBgMusicList] = useState<BgMusic[]>([]);
  const [selectedBgMusic, setSelectedBgMusic] = useState<string | null>(null);
  const [bgMusicVolume, setBgMusicVolume] = useState(0.5);

  // 语速相关状态
  const [voiceSpeed, setVoiceSpeed] = useState(1.0);

  // 字幕模板相关状态
  const [subtitleTemplates, setSubtitleTemplates] = useState<SubtitleTemplate[]>([]);
  const [subtitleStyle, setSubtitleStyle] = useState<SubtitleStyle>({
    fontName: 'Arial',
    fontSize: 20,
    primaryColor: '#FFFFFF',
    outlineColor: '#000000',
    outlineWidth: 2,
    alignment: 'bottom',
    marginV: 20,
    bold: false,
    italic: false,
    templateId: 'default'
  });

  const sseReaderRef = useRef<ReadableStreamDefaultReader<Uint8Array> | null>(null);
  const stateMachine = useVideoPageStateMachine();
  const navigate = useNavigate();

  // 页面卸载时停止所有音频播放
  useEffect(() => {
    return () => {
      // 停止页面上所有正在播放的音频元素
      const allAudioElements = document.querySelectorAll('audio');
      allAudioElements.forEach(audio => {
        audio.pause();
        audio.currentTime = 0;
      });
    };
  }, []);

  // 组件挂载时获取图片尺寸
  useEffect(() => {
    const fetchImageSizes = async () => {
      try {
        const response = await getImageSizes();
        if (response.code === 200 && response.data?.sizes) {
          setImageSizes(response.data.sizes);
          if (response.data.sizes.length > 0) {
            // 确保resolution是返回的sizes数组中的第一个选项
            setResolution(response.data.sizes[0]);
          }
        } else {
          // 接口失败时使用默认尺寸
          const defaultSizes = ['1024x1024', '1024x1364', '864x1536', '1364x1024'];
          setImageSizes(defaultSizes);
          // 确保resolution是默认尺寸数组中的第一个选项
          setResolution(defaultSizes[0]);
        }
      } catch (error) {
        // 异常时使用默认尺寸
        const defaultSizes = ['1024x1024', '1024x1364', '864x1536', '1364x1024'];
        setImageSizes(defaultSizes);
        // 确保resolution是默认尺寸数组中的第一个选项
        setResolution(defaultSizes[0]);
      }
    };
    fetchImageSizes();
  }, []);

  // 组件挂载时获取音色列表
  useEffect(() => {
    const fetchVoices = async () => {
      try {
        const response = await getVoices();
        const responseData = response as any;

        let foundVoices: Array<{ voice_id: string; name: string; voice_type?: string }> = [];

        // 处理不同的API响应格式
        if (Array.isArray(responseData)) {
          foundVoices = responseData;
        } else if (responseData.voices) {
          foundVoices = responseData.voices;
        } else if (responseData.data?.voices) {
          foundVoices = responseData.data.voices;
        } else if (responseData.data?.data?.voices) {
          foundVoices = responseData.data.data.voices;
        }

        if (foundVoices.length > 0) {
          const formattedVoices = foundVoices
            .filter((voice: any) => voice.voice_id && voice.name)
            .map((voice: any) => ({
              voice_id: voice.voice_id,
              name: voice.name,
              voice_type: voice.voice_type || voice.voice_id,
              audio_url: voice.audio_url || ''
            }));

          setVoices(formattedVoices);
          setSelectedVoice(formattedVoices.length > 0 ? formattedVoices[0].voice_id : '');
        } else {
          // 如果API返回空列表或格式不符合预期，使用空数组
          setVoices([]);
          setSelectedVoice('');
        }
      } catch (error) {
        console.error('获取语音列表失败:', error);
        // 如果API请求失败，使用空数组
        setVoices([]);
        setSelectedVoice('');
      }
    };
    fetchVoices();
  }, []);

  // 组件挂载时获取背景音乐列表
  useEffect(() => {
    const fetchBgMusicList = async () => {
      try {
        const response = await getBgMusicList();
        const responseData = response as any;

        let foundBgMusicList: BgMusic[] = [];

        // 处理不同的API响应格式
        if (Array.isArray(responseData)) {
          foundBgMusicList = responseData;
        } else if (responseData.musicList) {
          foundBgMusicList = responseData.musicList;
        } else if (responseData.data?.musicList) {
          foundBgMusicList = responseData.data.musicList;
        } else if (responseData.data?.data?.musicList) {
          foundBgMusicList = responseData.data.data.musicList;
        }

        setBgMusicList(foundBgMusicList);
      } catch (error) {
        console.error('获取背景音乐列表失败:', error);
        // 如果API请求失败，使用空数组
        setBgMusicList([]);
      }
    };
    fetchBgMusicList();
  }, []);

  // 组件挂载时获取字幕模板列表
  useEffect(() => {
    const fetchSubtitleTemplates = async () => {
      try {
        const response = await getSubtitleTemplates();
        const templates = response.templates || [];
        setSubtitleTemplates(templates);
        console.log('📝 字幕模板列表加载成功:', templates.length, '个模板');
      } catch (error) {
        console.error('获取字幕模板列表失败:', error);
        // 如果API请求失败，使用空数组
        setSubtitleTemplates([]);
      }
    };
    fetchSubtitleTemplates();
  }, []);

  // 流式数据请求
  const streamChatRequest = async (content: string, isEmptyScript: boolean = false) => {
    console.log('[STATE_MACHINE] 🚀 streamChatRequest called, using state machine methods');

    // 确保所有配置选项都已正确设置
    // 质量默认值
    const finalQuality = quality || 'standard';
    // 分辨率默认值
    const finalResolution = resolution || (imageSizes.length > 0 ? imageSizes[0] : '864x1536');
    // 音色默认值
    const finalVoice = selectedVoice || (voices.length > 0 ? voices[0].voice_id : '');
    // 音色类型默认值
    const finalVoiceType = voices.find(v => v.voice_id === finalVoice)?.voice_type || finalVoice;

    // 点击发送后立即跳转到编辑页面，将输入内容和配置选项作为参数传递
    navigate('/production/video/edit', {
      state: {
        inputContent: content,
        quality: finalQuality,
        resolution: finalResolution,
        selectedVoice: finalVoice,
        selectedVoiceType: finalVoiceType,
        selectedBgMusic: selectedBgMusic,
        bgMusicVolume: bgMusicVolume,
        voiceSpeed: voiceSpeed,
        subtitleStyle: subtitleStyle,  // ← 修改：传递完整的 subtitleStyle (包含 templateId)
        isEmptyScript: isEmptyScript
      }
    });
  };

  // 保存分镜列表
  const saveSceneList = () => {
    // TODO: 实现分镜列表的保存逻辑，参考原VideoPage的实现
    console.log('saveSceneList called');
  };

  // 恢复输入状态
  const restoreInputState = () => {
    console.log('restoreInputState called');
    if (stateMachine.getInputBarCompatState()) {
      // 保存当前sceneList
      saveSceneList();

      // 取消SSE请求
      if (sseReaderRef.current) {
        sseReaderRef.current.cancel('User clicked restart button.');
        sseReaderRef.current = null;
      }

      // 通知状态机停止生成
      stateMachine.stopGeneration();
      // 重置sceneList，确保回到初始状态
      setSceneList([]);
    }
  };

  return (
    <div className="bg-gray-950 flex flex-row">
      {/* InputBar区域 */}
      <div className="w-[600px] h-[calc(100vh-70px)] flex flex-col">
        <InputBar
          inputContent={inputContent}
          setInputContent={setInputContent}
          quality={quality}
          setQuality={setQuality}
          resolution={resolution}
          setResolution={setResolution}
          imageSizes={imageSizes}
          // 音色相关props
          voices={voices}
          selectedVoice={selectedVoice}
          setSelectedVoice={setSelectedVoice}
          voiceSpeed={voiceSpeed}
          setVoiceSpeed={setVoiceSpeed}
          // 背景音乐相关props
          bgMusicList={bgMusicList}
          selectedBgMusic={selectedBgMusic}
          bgMusicVolume={bgMusicVolume}
          onBgMusicSelect={setSelectedBgMusic}
          onVolumeChange={setBgMusicVolume}
          // 字幕模板相关props
          subtitleTemplates={subtitleTemplates}
          subtitleStyle={subtitleStyle}
          onSubtitleStyleChange={setSubtitleStyle}
          stateMachine={stateMachine}
          streamChatRequest={streamChatRequest}
          saveSceneList={saveSceneList}
          restoreInputState={restoreInputState}
          sseReaderRef={sseReaderRef}
          setSceneList={setSceneList}
        />
      </div>

      {/* Tutorial区域 */}
      <div className="flex-1">
        <Tutorial showTutorial={showTutorial} />
      </div>
    </div>
  );
};

export default VideoInputPage;
